import { ChevronDown, ChevronUp } from "lucide-react"

type StatusFilter = "active" | "closed"
export type SortDirection = "asc" | "desc"

interface PositionsHeaderProps {
	statusFilter: StatusFilter
	onStatusFilterChange: (status: StatusFilter) => void
	sortDirection?: SortDirection
	onSort?: () => void
}

export function PositionsHeader({
	statusFilter,
	onStatusFilterChange,
	sortDirection = "desc",
	onSort,
}: PositionsHeaderProps) {
	// Filter button component
	const FilterButton = ({ status, label }: { status: StatusFilter; label: string }) => (
		<button
			className={`rounded-lg px-3 py-1.5 font-medium text-sm transition-colors ${
				statusFilter === status
					? "bg-text-primary text-bg-primary"
					: "bg-bg-secondary/50 text-text-muted hover:bg-bg-secondary hover:text-text-primary dark:text-white/50 dark:hover:text-white"
			}`}
			onClick={() => onStatusFilterChange(status)}
			type="button"
		>
			{label}
		</button>
	)

	// Sort button component
	const SortButton = ({ className = "" }: { className?: string }) => (
		<button
			aria-label={`Sort by value, currently ${sortDirection === "asc" ? "ascending" : "descending"}`}
			className={`flex items-center gap-1 text-text-muted transition-colors hover:text-text-primary dark:text-white/50 dark:hover:text-white ${className}`}
			onClick={onSort}
			type="button"
		>
			<span>Value</span>
			{sortDirection === "asc" ? (
				<ChevronUp aria-hidden="true" className="size-3.5" />
			) : (
				<ChevronDown aria-hidden="true" className="size-3.5" />
			)}
		</button>
	)

	if (statusFilter === "closed") {
		return (
			<>
				{/* Mobile: filters left, sort right */}
				<div className="mb-3 flex items-center justify-between gap-2 sm:hidden">
					<div className="flex items-center gap-1.5">
						<FilterButton label="Active" status="active" />
						<FilterButton label="Closed" status="closed" />
					</div>
					<SortButton className="rounded-lg bg-bg-secondary/50 px-2.5 py-1.5 font-medium text-xs" />
				</div>

				{/* Desktop: filters row then header row */}
				<div className="hidden sm:block">
					<div className="mb-3 flex items-center gap-1.5">
						<FilterButton label="Active" status="active" />
						<FilterButton label="Closed" status="closed" />
					</div>
					<div className="mb-3 flex items-center gap-3 rounded-lg bg-bg-secondary/50 px-4 py-2 font-medium text-text-muted text-xs dark:text-white/50">
						{/* Result column */}
						<div className="w-16 shrink-0">Result</div>
						{/* Avatar spacer */}
						<div className="size-8 shrink-0" />
						{/* Market column */}
						<div className="min-w-0 flex-1">Market</div>
						{/* Value column - sortable */}
						<SortButton className="w-28 shrink-0 justify-end" />
						{/* Action spacer */}
						<div className="w-10 shrink-0" />
					</div>
				</div>
			</>
		)
	}

	// Active positions
	return (
		<>
			{/* Mobile: filters left, sort right */}
			<div className="mb-3 flex items-center justify-between gap-2 sm:hidden">
				<div className="flex items-center gap-1.5">
					<FilterButton label="Active" status="active" />
					<FilterButton label="Closed" status="closed" />
				</div>
				<SortButton className="rounded-lg bg-bg-secondary/50 px-2.5 py-1.5 font-medium text-xs" />
			</div>

			{/* Desktop: filters row then header row */}
			<div className="hidden sm:block">
				<div className="mb-3 flex items-center gap-1.5">
					<FilterButton label="Active" status="active" />
					<FilterButton label="Closed" status="closed" />
				</div>
				<div className="mb-3 flex items-center gap-3 rounded-lg bg-bg-secondary/50 px-4 py-2 font-medium text-text-muted text-xs dark:text-white/50">
					{/* Avatar spacer */}
					<div className="size-8 shrink-0" />
					{/* Market column */}
					<div className="min-w-0 flex-1">Market</div>
					{/* Avg column */}
					<div className="w-16 shrink-0 text-right">Avg</div>
					{/* Current column */}
					<div className="w-16 shrink-0 text-right">Current</div>
					{/* Value column - sortable */}
					<SortButton className="w-28 shrink-0 justify-end" />
					{/* Action spacer */}
					<div className="w-10 shrink-0" />
				</div>
			</div>
		</>
	)
}
